/**
 * A word-level diff for the Refine panel and the import review: the longest
 * common subsequence of words, so a reworded sentence marks the words that
 * changed and keeps the rest. Pure, no DOM.
 */

export type DiffKind = 'same' | 'del' | 'ins';

export interface DiffPart {
  kind: DiffKind;
  text: string;
}

/** Past this many word pairs the table would cost too much; the texts are shown as replaced whole. */
const MAX_CELLS = 4_000_000;

/** Each word with the space after it (and any leading space), so joining the parts gives the text back. */
function tokens(text: string): string[] {
  return text.match(/\S+\s*|\s+/g) ?? [];
}

/** Longest-common-subsequence lengths of the suffixes: lcs[i][j] for a[i..] and b[j..]. */
function suffixTable(a: string[], b: string[]): Table {
  const width = b.length + 1;
  const cells = new Uint32Array((a.length + 1) * width);
  const at = (i: number, j: number) => cells[i * width + j] ?? 0;
  for (let i = a.length - 1; i >= 0; i -= 1) {
    for (let j = b.length - 1; j >= 0; j -= 1) {
      cells[i * width + j] = a[i] === b[j] ? at(i + 1, j + 1) + 1 : Math.max(at(i + 1, j), at(i, j + 1));
    }
  }
  return at;
}

type Table = (i: number, j: number) => number;

/** Joins neighbours of the same kind, so "a b" is one part, not three. */
function merge(parts: DiffPart[]): DiffPart[] {
  const out: DiffPart[] = [];
  for (const part of parts) {
    const last = out[out.length - 1];
    if (last && last.kind === part.kind) last.text += part.text;
    else out.push({ ...part });
  }
  return out;
}

function replaced(before: string, after: string): DiffPart[] {
  const parts: DiffPart[] = [];
  if (before) parts.push({ kind: 'del', text: before });
  if (after) parts.push({ kind: 'ins', text: after });
  return parts;
}

/** Walks both texts along the table: shared words stay, the longer remainder goes first. */
function walk(a: string[], b: string[], lcs: Table): DiffPart[] {
  const parts: DiffPart[] = [];
  let i = 0;
  let j = 0;
  while (i < a.length || j < b.length) {
    const left = a[i];
    const right = b[j];
    if (left !== undefined && left === right) {
      parts.push({ kind: 'same', text: left });
      i += 1;
      j += 1;
    } else if (left !== undefined && (right === undefined || lcs(i + 1, j) >= lcs(i, j + 1))) {
      parts.push({ kind: 'del', text: left });
      i += 1;
    } else if (right !== undefined) {
      parts.push({ kind: 'ins', text: right });
      j += 1;
    }
  }
  return merge(parts);
}

/** How many tokens the two lists share at the start (and, within what is left, at the end). */
function sharedEnds(a: string[], b: string[]): [number, number] {
  const limit = Math.min(a.length, b.length);
  let head = 0;
  while (head < limit && a[head] === b[head]) head += 1;
  let tail = 0;
  while (tail < limit - head && a[a.length - 1 - tail] === b[b.length - 1 - tail]) tail += 1;
  return [head, tail];
}

export function diffWords(before: string, after: string): DiffPart[] {
  if (before === after) return before ? [{ kind: 'same', text: before }] : [];
  const a = tokens(before);
  const b = tokens(after);
  // Edits are usually local: only the middle, between the shared ends, needs the table.
  const [head, tail] = sharedEnds(a, b);
  const midA = a.slice(head, a.length - tail);
  const midB = b.slice(head, b.length - tail);
  const middle =
    midA.length * midB.length > MAX_CELLS
      ? replaced(midA.join(''), midB.join(''))
      : walk(midA, midB, suffixTable(midA, midB));
  const parts: DiffPart[] = [];
  if (head) parts.push({ kind: 'same', text: a.slice(0, head).join('') });
  parts.push(...middle);
  if (tail) parts.push({ kind: 'same', text: a.slice(a.length - tail).join('') });
  return merge(parts);
}
